import { makeAutoObservable, reaction } from "mobx";
import { UserType } from "../Models/UserType";
import { toast } from "react-toastify";
import agent from "../Api/agent";
import { RSVPFormValues } from "../Models/RSVPFormValues";
import { ContactFormValues } from "../Models/ContactFormValues";

export default class GeneralStore {
    isMobile : boolean = false;
    userType : UserType | undefined = window.localStorage.getItem("guestType") as UserType | undefined;
    showFooterAndNav : boolean = true;

    token : string | null = window.localStorage.getItem('token');
    loginFormLoading : boolean = false;

    rsvpFormLoading : boolean = false;
    rsvpFormVals : RSVPFormValues = {
        firstName: "",
        lastName: "",
        allergiesOrDietaryRequirements: "",
        starter: "",
        main: "",
        dessert: "",
        attending: undefined,
        userType: this.userType,
        isChildMeal: "false",
        songRequest: "",
        otherRequests: ""
    }
    hasSubmittedRSVP : boolean = true;
    isNotComing : boolean = false;

    contactFormLoading : boolean = false;
    contactFormVals : ContactFormValues = {
        name: "",
        email: "",
        question: ""
    }

    constructor() {
        makeAutoObservable(this);

        reaction(
            () => this.token,
            token => {
                if(token) {
                    window.localStorage.setItem('token', token);
                } else {
                    window.localStorage.removeItem('token');
                }
            }
        )

        if (this.token) {
            this.showFooterAndNav = true;
        }
    }

    setIsMobile = (windowSize : number) => {
        if(windowSize < 768){
            this.isMobile = true;
        }
        else {
            this.isMobile = false;
        }
    }

    getUserType = (): void => {
        const storedUserType = localStorage.getItem("guestType");
    
        if (storedUserType === "dayGuest" || storedUserType === "eveningGuest") {
            this.userType = storedUserType as UserType;
            this.showFooterAndNav = true;
            return;
        } else {
            // this.redirectToLandingPage();
        }

        return;
    }

    validateAccessCode = async (code : string) : Promise<boolean> => {
        const codeUpper = code.toUpperCase();
        this.setLoginFormLoading(true);
        let success = false;
        try {
            const response = await agent.api.login(codeUpper);
            if (response.status !== 200) {
                toast.error("Invalid access code");
                success = false;
            }
            else {
                this.setToken(response.data.token);

                if (response.data.userType === UserType.DayGuest) {
                    this.setGuestType(UserType.DayGuest);
                } else if (response.data.userType === UserType.EveningGuest) {
                    this.setGuestType(UserType.EveningGuest);
                }

                this.setShowFooterAndNav(true);
                success = true;
            }
        }
        catch {
            toast.error("Invalid access code");
            success = false;
        }
        finally {
            this.setLoginFormLoading(false);
        }

        return success;
    }

    postRSVP = async (body : RSVPFormValues) : Promise<boolean> => {
        this.setRSVPFormLoading(true);
        let success = false;
        try {
            const response = await agent.api.rsvp(body);
            if (response.status !== 201) {
                toast.error("Failed to RSVP");
                success = false;
            }
            else {
                if (body.attending && body.attending === "false") {
                    this.setIsNotComing(true);
                }
                toast.success("RSVP successful!");
                this.emptyRSVPFormVals();
                this.setHasSubmittedRSVP(true);
                success = true;
            }
        }
        catch {
            toast.error("Something went wrong, please login again and try again, otherwise contact Ryan using info on the invite.");
            this.resetUser();
            success = false;
        }
        finally {
            this.setRSVPFormLoading(false);
        }

        return success;
    }

    postContact = async (body : ContactFormValues) : Promise<boolean> => {
        this.setContactFormLoading(true);
        let success = false;
        try {
            const response = await agent.api.contact(body);
            if (response.status !== 200) {
                if (response.status === 500) {
                    toast.error("Failed to send message, please email us directly");
                }
                toast.error("Failed to send message");
                success = false;
            }
            else {
                this.emptyContactFormVals();
                toast.success("Message sent!");
                success = true;
            }
        }
        catch {
            toast.error("Failed to send message");
            success = false;
        }
        finally {
            this.setContactFormLoading(false);
        }

        return success;
    }


    removeUserType = (): void => {
        localStorage.removeItem("guestType");
        this.userType = undefined;
        this.showFooterAndNav = false;
    }

    setGuestType = (userType : UserType) => {
        localStorage.setItem("guestType", userType);
        this.userType = userType;
    }

    setShowFooterAndNav  = (show : boolean) => {
        this.showFooterAndNav = show;
    }

    setToken = (token : string) => { 
        if (token) {
            window.localStorage.setItem('token', token);
        }     
        this.token = token;
    }

    resetUser = () => {
        window.localStorage.removeItem('token');
        this.removeUserType();
        this.token = null;
    }
    
    setLoginFormLoading = (loading : boolean) => {
        this.loginFormLoading = loading;
    }

    setRSVPFormLoading = (loading : boolean) => {
        this.rsvpFormLoading = loading;
    }

    setContactFormLoading = (loading : boolean) => {
        this.contactFormLoading = loading;
    }

    emptyContactFormVals = () => {
        this.contactFormVals = {
            name: "",
            email: "",
            question: ""
        }
    }

    emptyRSVPFormVals = () => {
        this.rsvpFormVals = {
            firstName: "",
            lastName: "",
            allergiesOrDietaryRequirements: "",
            starter: "",
            main: "",
            dessert: "",
            attending: undefined,
            userType: UserType.DayGuest,
            isChildMeal: "",
            songRequest: "",
            otherRequests: ""
        }
    }

    setIsNotComing = (notComing : boolean) => {
        this.isNotComing = notComing;
    }

    setHasSubmittedRSVP = (submitted : boolean) => {
        this.hasSubmittedRSVP = submitted;
    }
}

